'use client';

import React, { type MouseEvent } from 'react';

import clsx from 'clsx';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

import styles from './styles.module.css';

import {
  campaignTitleParamName,
  moduleOriginParamName,
} from '@/modules/activityTracker/constants';
import { isServerCheck } from '@/modules/env/isServerCheck';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { appendParams } from '@/modules/routing/appendParams/appendParams.ts';
import { ROUTE_CHANGE_START_EVENT } from '@/modules/routing/constants';
import {
  ensureTrailingSlash,
  getUpdatedQueryParam,
  replaceUsernameInPath,
} from '@/modules/routing/helpers';

type Props = React.ComponentProps<typeof Link>;

export function ExtendedLink(props: Props) {
  const { href, as, onClick, className, ref, prefetch = false } = props;
  const { location, defaultLocation, localisedRoutes } = useCurrentLocation();

  const linkProps = {
    ...props,
    className: clsx(styles.unstyledLink, className),
  };

  let modifiedHref = '';
  const originalHref = typeof href === 'string' ? href : href.href || '#';

  if (typeof as === 'string') {
    modifiedHref = as;
    const queryParamsIndex = originalHref?.indexOf('?');
    if (queryParamsIndex > -1) {
      modifiedHref += originalHref.substring(queryParamsIndex);
    }
  } else {
    modifiedHref = originalHref;
  }

  const [_, ...pathSegments] = modifiedHref.split('/');
  const [rootPath] = pathSegments;

  const isLocalisedRoute =
    location !== defaultLocation &&
    localisedRoutes.some(
      (route) =>
        route === rootPath || (route === 'home' && modifiedHref === '/')
    );

  const searchParams = useSearchParams();

  // always carry though the module origin & campaignTitle if they exists
  const [moduleOrigin, campaignTitle] = [
    getUpdatedQueryParam(searchParams, modifiedHref, moduleOriginParamName),
    getUpdatedQueryParam(searchParams, modifiedHref, campaignTitleParamName),
  ];

  const modifiedParams: [string, string][] = [];
  if (moduleOrigin) {
    modifiedParams.push([moduleOriginParamName, moduleOrigin]);
  }
  if (campaignTitle) {
    modifiedParams.push([campaignTitleParamName, campaignTitle]);
  }

  if (modifiedParams.length > 0) {
    modifiedHref = appendParams(modifiedHref, ...modifiedParams);
  }

  modifiedHref = replaceUsernameInPath(modifiedHref);

  function modifiedOnClick(e: MouseEvent<HTMLAnchorElement>) {
    document.dispatchEvent(new CustomEvent(ROUTE_CHANGE_START_EVENT));
    onClick?.(e);
  }

  const modifiedLinkProps = {
    ...linkProps,
    href: isLocalisedRoute ? `/${location}${modifiedHref}` : modifiedHref,
  };

  return (
    <Link
      ref={ref as React.Ref<HTMLAnchorElement>}
      prefetch={prefetch}
      {...modifiedLinkProps}
      {...(!isServerCheck() ? { onClick: modifiedOnClick } : {})}
      href={ensureTrailingSlash(String(modifiedLinkProps.href))}
      as={
        modifiedLinkProps?.as
          ? ensureTrailingSlash(String(modifiedLinkProps.as))
          : undefined
      }
    />
  );
}
